import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { RecipeCardsFinderService } from '../../../pages/marketing/core/services/RecipeCardsFinderService';
import { IRecipeCard } from '../../../pages/marketing/core/interfaces/IRecipeCard';
import { OpenPdfDirective } from '../../../pages/labels-printer/core/directives/open-pdf.directive';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-recipe-card-finder',
  standalone: false,
  templateUrl: './recipe-card-finder.component.html',
  styleUrl: './recipe-card-finder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OpenPdfDirective]
})
export class RecipeCardFinderComponent implements OnInit {

  @Input() focusedAsDefault = true;

  public recipeCards: IRecipeCard[] = [];
  public totalResults = 0;
  public loading = false;
  public searched = false;
  public lastTerm = '';
  public initialTerm = '';
  public readonly search$ = new Subject<string>();

  private readonly _storageKey = 'recipe-card-finder:q';
  private readonly _recipeCardsFinderService = inject(RecipeCardsFinderService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);

  public constructor() {
    this.search$.pipe(
      map(term => (term || '').trim()),
      tap(term => {
        this.loading = true;
        this.lastTerm = term;
        this.persistTerm(term);
        this._cdr.markForCheck();
      }),
      switchMap(term => this.performSearch(term).pipe(
        catchError(() => {
          this.totalResults = 0;
          return of([] as IRecipeCard[]);
        })
      ))
    ).subscribe(cards => {
      this.recipeCards = cards || [];
      this.loading = false;
      this.searched = true;
      this._cdr.markForCheck();
    });
  }

  public ngOnInit(): void {
    const term = (this._route.snapshot.queryParamMap.get('q') || this.readStoredTerm()).trim();
    if (!term) {
      return;
    }
    this.initialTerm = term;
    this.search$.next(term);
  }

  private persistTerm(term: string): void {
    if (term) {
      sessionStorage.setItem(this._storageKey, term);
    } else {
      sessionStorage.removeItem(this._storageKey);
    }

    if (!this.isFinderPage()) {
      return;
    }

    const current = this._route.snapshot.queryParamMap.get('q') || '';
    if (current === term) {
      return;
    }

    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { q: term || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private readStoredTerm(): string {
    return sessionStorage.getItem(this._storageKey) || '';
  }

  private isFinderPage(): boolean {
    return this._router.url.split('?')[0].endsWith('/recipe-card-finder');
  }

  private performSearch(term: string): Observable<IRecipeCard[]> {
    if (!term) {
      this.totalResults = 0;
      return of([]);
    }

    if (/^\d{5,}$/.test(term)) {
      return this._recipeCardsFinderService.getByEan(term).pipe(
        map(cards => cards || []),
        tap(cards => this.totalResults = cards.length)
      );
    }

    if (/^\d{1,4}$/.test(term)) {
      return forkJoin({
        byNumber: this._recipeCardsFinderService.getByNumber(+term).pipe(
          map(card => card ? [card] : []),
          catchError(() => of([] as IRecipeCard[]))
        ),
        byText: this.searchByText(term).pipe(
          catchError(() => of({ cards: [] as IRecipeCard[], total: 0 }))
        )
      }).pipe(
        map(({ byNumber, byText }) => {
          const merged = this.mergeById([...byNumber, ...byText.cards]);
          const extra = byNumber.filter(card =>
            !byText.cards.some(existing => this.cardKey(existing) === this.cardKey(card))
          ).length;
          this.totalResults = Math.max(merged.length, byText.total + extra);
          return merged;
        })
      );
    }

    return this.searchByText(term).pipe(
      tap(res => this.totalResults = res.total),
      map(res => res.cards)
    );
  }

  private searchByText(term: string): Observable<{ cards: IRecipeCard[]; total: number }> {
    return this._recipeCardsFinderService.getQueryableAll(
      1, 24, 'number', 'Ascend', [],
      [{ key: 'q', value: [term] }],
      []
    ).pipe(map(res => ({
      cards: res?.results || [],
      total: res?.info?.totalResults ?? (res?.results || []).length
    })));
  }

  private mergeById(cards: IRecipeCard[]): IRecipeCard[] {
    const map = new Map<number, IRecipeCard>();
    cards.forEach(card => {
      const key = this.cardKey(card);
      if (!map.has(key)) {
        map.set(key, card);
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.number || 0) - (b.number || 0));
  }

  private cardKey(card: IRecipeCard): number {
    return card.id ?? card.number;
  }

  public keywords(card: IRecipeCard): string[] {
    return (card.keywords || '')
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
  }

  public ingredients(card: IRecipeCard): string[] {
    if (card.articles?.length) {
      return card.articles.map(a => a.articleName).filter(Boolean);
    }

    return (card.ingredientsRaw || '')
      .split('\n')
      .map(i => i.trim())
      .filter(i => i && !this.isJunkIngredient(i, card));
  }

  public formatMinutes(value: string): string {
    if (!value) {
      return '–';
    }
    return /min|stunde|h/i.test(value) ? value : `${value} Min.`;
  }

  public openPdfKey(card: IRecipeCard): string | null {
    return card.documentKey || card.frontRecipeKey || card.backRecipeKey || null;
  }

  public backPdfKey(card: IRecipeCard): string | null {
    const back = card.backRecipeKey || null;
    if (!back || back === this.openPdfKey(card)) {
      return null;
    }
    return back;
  }

  public printerLink(card: IRecipeCard): string | null {
    if (!card.cardArticleId || !card.cardArticleVariantId) {
      return null;
    }
    return `/recipe-cards-printer/recipe-cards/${card.cardArticleId}/${card.cardArticleVariantId}`;
  }

  public shopwareLink(card: IRecipeCard): string | null {
    if (!card.cardArticleId) {
      return null;
    }
    const base = (environment.shopUrls?.[0] || '').replace(/\/$/, '');
    if (!base) {
      return null;
    }
    return `${base}/detail/index/sArticle/${card.cardArticleId}`;
  }

  private isJunkIngredient(value: string, card: IRecipeCard): boolean {
    const lower = value.toLowerCase();
    if (lower.includes('will be replaced')) {
      return true;
    }
    const title = (card.title || '').toLowerCase();
    return lower.length > 10 && title.includes(lower);
  }
}
